import React, { useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
} from '@mui/material';
import { useForm, useFieldArray } from 'react-hook-form';
import { PrimaryButton, SecondaryButton } from '@/components';
import { ConsumerFormFields, ConsumerFormData } from './ConsumerFormFields';

export interface ConsumerCreateDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: ConsumerFormData) => Promise<void>;
}

export const ConsumerCreateDialog: React.FC<ConsumerCreateDialogProps> = ({
  open,
  onClose,
  onSubmit,
}) => {
  const form = useForm<ConsumerFormData>({
    defaultValues: {
      name: '',
      webhook: {
        url: '',
        method: 'POST',
        headers: [],
        queryParameters: [],
      },
      batchCapacity: 10,
      batchMinInterval: 1000,
    }
  });

  const { isSubmitting } = form.formState;
  const { fields: headerFields, append: appendHeader, remove: removeHeader } = useFieldArray({
    control: form.control,
    name: 'webhook.headers',
  });

  const { fields: queryParamFields, append: appendQueryParam, remove: removeQueryParam } = useFieldArray({
    control: form.control,
    name: 'webhook.queryParameters',
  });

  useEffect(() => {
    if (open) {
      form.reset({
        name: '',
        webhook: {
          url: '',
          method: 'POST',
          headers: [],
          queryParameters: [],
        },
        batchCapacity: 10,
        batchMinInterval: 1000,
      });
    }
  }, [open, form]);

  const onFormSubmit = async (data: ConsumerFormData) => {
    try {
      await onSubmit(data);
      form.reset();
      onClose();
    } catch (error) {
      console.error('Form submission failed:', error);
    }
  };

  const handleClose = () => {
    form.reset();
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Create New Consumer WEBHOOK</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 1 }} component="form" onSubmit={form.handleSubmit(onFormSubmit)}>
          <ConsumerFormFields
            control={form.control}
            errors={form.formState.errors}
            headerFields={headerFields}
            appendHeader={appendHeader}
            removeHeader={removeHeader}
            queryParamFields={queryParamFields}
            appendQueryParam={appendQueryParam}
            removeQueryParam={removeQueryParam}
            autoFocus
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <SecondaryButton onClick={handleClose} disabled={isSubmitting}>Cancel</SecondaryButton>
        <PrimaryButton onClick={form.handleSubmit(onFormSubmit)} type="submit" disabled={isSubmitting}>
          Create
        </PrimaryButton>
      </DialogActions>
    </Dialog>
  );
};
